import React, {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useContext,
} from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import {
    Typography,
    TableContainer,
} from "@material-ui/core";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import RatingModal from "../../components/RatingModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import Pagination from "@material-ui/lab/Pagination";
import { socketManager } from "../../context/Socket/SocketContext";
import AddIcon from "@material-ui/icons/Add";

const reducer = (state, action) => {
    if (action.type === "LOAD_RATINGS") {
        return action.payload;
    }


    if (action.type === "UPDATE_RATINGS") {
        const rating = action.payload;
        const ratingIndex = state.findIndex((s) => s.id === rating.id);

        if (ratingIndex !== -1) {
            state[ratingIndex] = rating;
            return [...state];
        } else {
            return [rating, ...state];
        }
    }

    if (action.type === "DELETE_TAG") {
        const ratingId = action.payload;

        const ratingIndex = state.findIndex((s) => s.id === ratingId);
        if (ratingIndex !== -1) {
            state.splice(ratingIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(4),
        background: "#f8fafc",
        minHeight: "calc(100vh - 100px)",
    },
    header: {
        marginBottom: theme.spacing(4),
    },
    headerTop: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(3),
    },
    headerTitle: {
        fontSize: "2.5rem",
        fontWeight: 800,
        background: "linear-gradient(45deg, #1a1a1a 30%, #666666 90%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        letterSpacing: "-0.5px",
    },
    statsCard: {
        background: "linear-gradient(135deg, #000 0%, #333 100%)",
        borderRadius: "20px",
        padding: theme.spacing(3),
        color: "#fff",
        display: "inline-flex",
        alignItems: "center",
        gap: theme.spacing(2),
        "& span": {
            fontSize: "2rem",
            fontWeight: 700,
        },
        "& p": {
            margin: 0,
            opacity: 0.7,
            fontSize: "0.875rem",
        }
    },
    newButton: {
        background: "transparent",
        border: "2px solid #000",
        borderRadius: "16px",
        padding: "12px 32px",
        color: "#000",
        textTransform: "none",
        fontSize: "1rem",
        fontWeight: 600,
        transition: "all 0.3s ease",
        "&:hover": {
            background: "#000",
            color: "#fff",
            transform: "translateY(-2px)",
        }
    },
    content: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: theme.spacing(3),
        padding: theme.spacing(1),
    },
    ratingCard: {
        background: "#fff",
        borderRadius: "20px",
        padding: theme.spacing(3),
        transition: "all 0.3s ease",
        border: "1px solid rgba(0,0,0,0.05)",
        position: "relative",
        overflow: "hidden",
        "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 12px 24px rgba(0,0,0,0.05)",
            "&:before": {
                transform: "translateX(0)",
            }
        },
        "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "4px",
            height: "100%",
            background: "#000",
            transform: "translateX(-100%)",
            transition: "all 0.3s ease",
        }
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: theme.spacing(2),
    },
    authorInfo: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
    authorName: {
        fontSize: "1.25rem",
        fontWeight: 700,
        color: "#000",
        marginBottom: theme.spacing(0.5),
    },
    cardActions: {
        display: "flex",
        gap: theme.spacing(1),
    },
    actionButton: {
        minWidth: "40px",
        height: "40px",
        borderRadius: "12px",
        padding: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.3s ease",
        border: "1px solid rgba(0,0,0,0.1)",
    },
    editButton: {
        color: "#000",
        "&:hover": {
            background: "#f8fafc",
            transform: "translateY(-2px)",
        }
    },
    deleteButton: {
        color: "#e11d48",
        "&:hover": {
            background: "#fff1f2",
            transform: "translateY(-2px)",
        }
    },
    checkIcon: {
        display: "inline-flex",
        alignItems: "center",
        padding: "6px 12px",
        background: "#f0fdf4",
        color: "#16a34a",
        borderRadius: "20px",
        fontSize: "0.875rem",
        fontWeight: 600,
        gap: theme.spacing(1),
    },
    pagination: {
        marginTop: theme.spacing(4),
        display: "flex",
        justifyContent: "center",
        "& .MuiPaginationItem-root": {
            border: "none",
            background: "transparent",
            fontSize: "1rem",
            fontWeight: 600,
            color: "#666",
            "&.Mui-selected": {
                background: "#000",
                color: "#fff",
                "&:hover": {
                    background: "#000",
                }
            },
            "&:hover": {
                background: "rgba(0,0,0,0.05)",
            }
        }
    },
    emptyMessage: {
        textAlign: "center",
        padding: theme.spacing(8),
        background: "#fff",
        borderRadius: "20px",
        border: "2px dashed rgba(0,0,0,0.1)",
        color: "#666",
        fontSize: "1.1rem",
        fontWeight: 500,
        gridColumn: "1 / -1",
    }
}));

const Ratings = () => {
    const classes = useStyles();

    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRatings, setTotalRatings] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [selectedRating, setSelectedRating] = useState(null);
    const [deletingRating, setDeletingRating] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [searchParam, setSearchParam] = useState("");
    const [ratings, dispatch] = useReducer(reducer, []);
    const [ratingModalOpen, setRatingModalOpen] = useState(false);

    const fetchRatings = useCallback(async () => {
        try {
            const { data } = await api.get("/ratings/", {
                params: { searchParam, pageNumber },
            });
            dispatch({ type: "LOAD_RATINGS", payload: data.ratings });
            setTotalRatings(data.count);
            setTotalPages(Math.ceil(data.count / 20));
            setHasMore(data.hasMore);
            setLoading(false);
        } catch (err) {
            toastError(err);
        }
    }, [searchParam, pageNumber]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [searchParam]);

    useEffect(() => {
        setLoading(true);
        const delayDebounceFn = setTimeout(() => {
            fetchRatings();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchParam, pageNumber, fetchRatings]);

    useEffect(() => {
        const socket = socketManager.getSocket(user.companyId);


        socket.on("user", (data) => {
            if (data.action === "update" || data.action === "create") {
                dispatch({ type: "UPDATE_RATINGS", payload: data.ratings });
            }

            if (data.action === "delete") {
                dispatch({ type: "DELETE_USER", payload: +data.ratingId });
            }
        });

        return () => {
            // //socket.off("user", (data) => {
            //     if (data.action === "update" || data.action === "create") {
            //         dispatch({ type: "UPDATE_RATINGS", payload: data.ratings });
            //     }

            //     if (data.action === "delete") {
            //         dispatch({ type: "DELETE_USER", payload: +data.ratingId });
            //     }
            // });
        };
    }, [user]);

    const handleOpenRatingModal = () => {
        setSelectedRating(null);
        setRatingModalOpen(true);
    };

    const handleCloseRatingModal = () => {
        setSelectedRating(null);
        setRatingModalOpen(false);
    };

    const handleSearch = (event) => {
        setSearchParam(event.target.value.toLowerCase());
    };

    const handleEditRating = (rating) => {
        setSelectedRating(rating);
        setRatingModalOpen(true);
    };

    const handleDeleteRating = async (ratingId) => {
        try {
            await api.delete(`/ratings/${ratingId}`);
            toast.success(i18n.t("ratings.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingRating(null);
        setSearchParam("");
        setPageNumber(1);

        dispatch({ type: "RESET" });
        setPageNumber(1);
        await fetchRatings();
    };

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };


    const handlePageChange = (event, value) => {
        setPageNumber(value);
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.headerTop}>
                    <Typography variant="h1" className={classes.headerTitle}>
                        {i18n.t("ratings.title")}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={handleOpenRatingModal}
                        className={classes.newButton}
                        startIcon={<AddIcon />}
                    >
                        {i18n.t("ratings.buttons.add")}
                    </Button>
                </div>
                <div className={classes.statsCard}>
                    <span>{ratings.length}</span>
                    <p>{ratings.length === 1 ? 'calificación' : 'calificaciones'}</p>
                </div>
            </div>

            <div className={classes.content}>
                {ratings.length > 0 ? (
                    ratings.map((rating) => (
                        <Paper key={rating.id} className={classes.ratingCard} elevation={0}>
                            <div className={classes.cardHeader}>
                                <div className={classes.authorInfo}>
                                    <Typography className={classes.authorName}>
                                        {rating.name}
                                    </Typography>
                                    {rating.geral && (
                                        <div className={classes.checkIcon}>
                                            <CheckCircleIcon fontSize="small" />
                                            Geral
                                        </div>
                                    )}
                                </div>
                                <div className={classes.cardActions}>
                                    <IconButton
                                        className={`${classes.actionButton} ${classes.editButton}`}
                                        onClick={() => handleEditRating(rating)}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        className={`${classes.actionButton} ${classes.deleteButton}`}
                                        onClick={() => {
                                            setConfirmModalOpen(true);
                                            setDeletingRating(rating);
                                        }}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </div>
                            </div>
                        </Paper>
                    ))
                ) : (
                    <div className={classes.emptyMessage}>
                        Ninguna calificación encontrada
                    </div>
                )}
            </div>

            <div className={classes.pagination}>
                <Pagination
                    count={totalPages}
                    page={pageNumber}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                />
            </div>

            <ConfirmationModal
                title={deletingRating && `${i18n.t("ratings.confirmationModal.deleteTitle")}`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteRating(deletingRating.id)}
            >
                {i18n.t("ratings.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            
            <RatingModal
                open={ratingModalOpen}
                onClose={handleCloseRatingModal}
                reload={fetchRatings}
                aria-labelledby="form-dialog-title"
                ratingId={selectedRating && selectedRating.id}
            />
        </div>
    );
};

export default Ratings;
